import React, { useState, useMemo, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Select,
  MenuItem,
  TextField,
  IconButton,
  Paper,
  Fade,
} from '@mui/material';
import { Clear as ClearIcon, TrendingUp, TrendingDown, AccountBalanceWallet, ShowChart } from '@mui/icons-material';
import { LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useSnackbar } from 'notistack';
import { fetchTrades } from '../../store/Slices/aiTradingSlice';
import {useTheme} from '@mui/material';
// AI Trading Performance Dashboard Component
const AISection = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { aiBalances } = useSelector((state) => state.assets);
  const { trades, loading } = useSelector((state) => state.aiTrading);
  const light = theme.palette.mode === 'light';

  // State for filters
  const [timeRange, setTimeRange] = useState('30d');
  const [statusFilter, setStatusFilter] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [dateError, setDateError] = useState('');

  // Fetch trades on component mount
  useEffect(() => {
    dispatch(fetchTrades()).catch(() => {
      enqueueSnackbar('Failed to fetch trades', { variant: 'error' });
    });
  }, [dispatch, enqueueSnackbar]);

  // Validate date input
  const validateDate = (dateStr) => {
    if (!dateStr) return true;
    const date = new Date(dateStr);
    return date instanceof Date && !isNaN(date);
  };

  // Filter trades based on time range and status
  const filteredTrades = useMemo(() => {
    let filtered = trades;
    const now = new Date();

    if (timeRange !== 'custom') {
      const days = parseInt(timeRange) || 365 * 100; // 'all' = 100 years
      const start = new Date();
      start.setDate(now.getDate() - days);
      filtered = filtered.filter((trade) => new Date(trade.created_at) >= start);
    } else if (startDate || endDate) {
      filtered = filtered.filter((trade) => {
        const tradeDate = new Date(trade.created_at);
        let matches = true;
        if (startDate && validateDate(startDate)) {
          matches = matches && tradeDate >= new Date(startDate);
        }
        if (endDate && validateDate(endDate)) {
          matches = matches && tradeDate <= new Date(endDate);
        }
        return matches;
      });
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter((trade) => trade.status.toLowerCase() === statusFilter);
    }

    return filtered;
  }, [trades, timeRange, statusFilter, startDate, endDate]);

  // Calculate performance metrics
  const performanceMetrics = useMemo(() => {
    const completedTrades = filteredTrades.filter(
      (trade) => trade.status === 'Completed' || trade.status === 'Stopped'
    );
    const totalTrades = completedTrades.length;
    const winningTrades = completedTrades.filter((trade) => trade.profit_loss > 0).length;
    const winRate = totalTrades ? (winningTrades / totalTrades) * 100 : 0;
    const totalProfitLoss = completedTrades.reduce((sum, trade) => sum + trade.profit_loss, 0);
    const totalInvestment = completedTrades.reduce((sum, trade) => sum + trade.investment, 0);
    const roi = totalInvestment ? (totalProfitLoss / totalInvestment) * 100 : 0;
    const averageProfitPerTrade = totalTrades ? totalProfitLoss / totalTrades : 0;

    // Cumulative performance data for line chart
    const cumulativeData = [];
    let cumulativeProfit = 0;
    completedTrades
      .sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
      .forEach((trade) => {
        cumulativeProfit += trade.profit_loss;
        cumulativeData.push({
          date: new Date(trade.created_at).toLocaleDateString(),
          profit: cumulativeProfit,
        });
      });

    // Pie chart data for trade status distribution
    const pieData = [
      { name: 'Winning Trades', value: winningTrades },
      { name: 'Losing Trades', value: totalTrades - winningTrades },
      { name: 'Active Trades', value: filteredTrades.filter((trade) => trade.status === 'Active').length },
    ].filter((entry) => entry.value > 0);

    return {
      winRate: winRate.toFixed(2),
      totalProfitLoss: totalProfitLoss.toFixed(2),
      roi: roi.toFixed(2),
      averageProfitPerTrade: averageProfitPerTrade.toFixed(2),
      totalTrades,
      cumulativeData,
      pieData,
    };
  }, [filteredTrades]);

  // Handle date input changes
  const handleDateChange = (value, isStart) => {
    setDateError('');
    if (isStart) {
      setStartDate(value);
    } else {
      setEndDate(value);
    }
    if (value && !validateDate(value)) {
      setDateError('Please enter a valid date.');
    }
  };

  // Reset all filters
  const resetFilters = () => {
    setTimeRange('30d');
    setStatusFilter('all');
    setStartDate('');
    setEndDate('');
    setDateError('');
  };

  // Dynamic colors for pie chart based on theme
  const COLORS = light
    ? ['#4caf50', '#ef5350', '#1976d2'] // Light theme: green, red, blue
    : ['#66bb6a', '#f44336', '#42a5f5']; // Dark theme: brighter green, darker red, lighter blue

  // Line chart stroke color based on theme
  const lineChartStroke = light ? '#1976d2' : '#42a5f5';

  return (
    <Fade in timeout={600}>
      <Box
        sx={{
          p: { xs: 2, sm: 3, md: 4 },
          bgcolor: light ? 'grey.50' : 'grey.900',
          borderRadius: 3,
          boxShadow: light ? '0 4px 20px rgba(0,0,0,0.05)' : '0 4px 20px rgba(0,0,0,0.3)',
          maxWidth: '1400px',
          mx: 'auto',
        }}
      >
        {/* Balance Card */}
        {loading ? (
          <Box sx={{ bgcolor: light ? 'grey.200' : 'grey.700', height: 180, borderRadius: 3, mb: 4 }} />
        ) : (
          <Card
            sx={{
              mb: 4,
              p: { xs: 2, sm: 3 },
              bgcolor: light ? '#fff' : '#1e1e2f',
              borderRadius: 3,
              boxShadow: '0 6px 24px rgba(0,0,0,0.15)',
              transition: 'transform 0.3s ease, box-shadow 0.3s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 12px 32px rgba(0,0,0,0.2)',
              },
            }}
          >
            <CardContent
              sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'center', gap: 2 }}
            >
              <Box sx={{ flexGrow: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <AccountBalanceWallet sx={{ color: '#4caf50', fontSize: 24 }} />
                  <Typography
                    variant="h6"
                    sx={{ color: light ? 'grey.700' : 'grey.300', fontWeight: 500 }}
                  >
                    AI Wallet Balance
                  </Typography>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    color: '#4caf50',
                    fontWeight: 700,
                    fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' },
                  }}
                >
                  {aiBalances.find((b) => b.asset_id?.symbol === 'USDT')?.balance.toFixed(2) || '0.00'} USDT
                </Typography>
              </Box>
              <Button
                variant="contained"
                sx={{
                  width: { xs: '100%', sm: 'auto' },
                  bgcolor: theme.palette.brand?.main || '#007bff',
                  color: '#fff',
                  px: 4,
                  py: 1.5,
                  borderRadius: 2,
                  fontWeight: 600,
                  textTransform: 'none',
                  '&:hover': {
                    bgcolor: theme.palette.brand?.dark || '#0056b3',
                    transform: 'scale(1.05)',
                    transition: 'transform 0.2s ease',
                  },
                }}
                onClick={() => navigate('/transfer')}
              >
                Transfer
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Filters */}
        <Paper
          sx={{
            p: { xs: 2, sm: 3 },
            mb: 4,
            bgcolor: light ? '#fff' : '#1e1e2f',
            borderRadius: 3,
            boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography
              variant="h6"
              sx={{ color: light ? 'grey.700' : 'grey.300', fontWeight: 500 }}
            >
              Filter Performance
            </Typography>
            <IconButton
              onClick={resetFilters}
              sx={{
                color: light ? 'grey.500' : 'grey.400',
                '&:hover': { color: '#ef5350' },
              }}
              aria-label="Reset filters"
            >
              <ClearIcon />
            </IconButton>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Select
                value={timeRange}
                onChange={(e) => setTimeRange(e.target.value)}
                fullWidth
                variant="outlined"
                sx={{
                  bgcolor: light ? '#f5f5f5' : '#2c2c4c',
                  color: light ? 'grey.900' : '#e0e0e0',
                  '& .MuiSvgIcon-root': { color: light ? 'inherit' : '#e0e0e0' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#007bff' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#007bff' },
                  borderRadius: 1.5,
                }}
                aria-label="Time range filter"
              >
                <MenuItem value="7d">Last 7 Days</MenuItem>
                <MenuItem value="30d">Last 30 Days</MenuItem>
                <MenuItem value="90d">Last 90 Days</MenuItem>
                <MenuItem value="custom">Custom Range</MenuItem>
                <MenuItem value="all">All Time</MenuItem>
              </Select>
            </Grid>
            {timeRange === 'custom' && (
              <>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="Start Date"
                    type="date"
                    value={startDate}
                    onChange={(e) => handleDateChange(e.target.value, true)}
                    fullWidth
                    variant="outlined"
                    InputLabelProps={{ shrink: true }}
                    sx={{
                      bgcolor: light ? '#f5f5f5' : '#2c2c4c',
                      '& input': { color: light ? 'grey.900' : '#e0e0e0' },
                      '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#007bff',
                      },
                      borderRadius: 1.5,
                      '& .Mui-focused label': { color: '#007bff' },
                    }}
                    aria-label="Start date filter"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="End Date"
                    type="date"
                    value={endDate}
                    onChange={(e) => handleDateChange(e.target.value, false)}
                    fullWidth
                    variant="outlined"
                    InputLabelProps={{ shrink: true }}
                    sx={{
                      bgcolor: light ? '#f5f5f5' : '#2c2c4c',
                      '& input': { color: light ? 'grey.900' : '#e0e0e0' },
                      '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#007bff',
                      },
                      borderRadius: 1.5,
                      '& .Mui-focused label': { color: '#007bff' },
                    }}
                    aria-label="End date filter"
                  />
                </Grid>
              </>
            )}
            <Grid item xs={12} sm={6} md={3}>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                fullWidth
                variant="outlined"
                sx={{
                  bgcolor: light ? '#f5f5f5' : '#2c2c4c',
                  color: light ? 'grey.900' : '#e0e0e0',
                  '& .MuiSvgIcon-root': { color: light ? 'inherit' : '#e0e0e0' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#007bff' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#007bff' },
                  borderRadius: 1.5,
                }}
                aria-label="Status filter"
              >
                <MenuItem value="all">All Statuses</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="stopped">Stopped</MenuItem>
              </Select>
            </Grid>
          </Grid>
          {dateError && (
            <Typography
              color="error"
              variant="body2"
              sx={{ mt: 2, fontSize: '0.875rem' }}
              role="alert"
            >
              {dateError}
            </Typography>
          )}
        </Paper>

        {/* Performance Metrics */}
        <Grid container spacing={2} sx={{ mb: 4 }}>
          {loading ? (
            Array(5)
              .fill(0)
              .map((_, i) => (
                <Grid item xs={12} sm={6} md={3} key={i}>
                  <Box sx={{ bgcolor: light ? 'grey.200' : 'grey.700', height: 120, borderRadius: 2 }} />
                </Grid>
              ))
          ) : (
            <>
              <Grid item xs={12} sm={6} md={3}>
                <Card
                  sx={{
                    borderRadius: 2,
                    bgcolor: light ? '#fff' : '#1e1e2f',
                    boxShadow: '0 6px 16px rgba(0,0,0,0.12)',
                    transition: 'transform 0.3s ease',
                    '&:hover': { transform: 'translateY(-4px)' },
                  }}
                >
                  <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <TrendingUp sx={{ color: performanceMetrics.totalProfitLoss >= 0 ? '#4caf50' : '#ef5350', fontSize: '1.5rem' }} />
                      <Typography variant="body2" sx={{ color: light ? 'grey.600' : 'grey.400' }}>
                        Total Profit/Loss
                      </Typography>
                    </Box>
                    <Typography
                      variant="h6"
                      sx={{
                        color: performanceMetrics.totalProfitLoss >= 0 ? '#4caf50' : '#ef5350',
                        fontWeight: 600,
                        fontSize: { xs: '1.25rem', sm: '1.5rem' },
                      }}
                    >
                      ${performanceMetrics.totalProfitLoss}
                      {performanceMetrics.totalProfitLoss >= 0 ? (
                        <TrendingUp sx={{ color: '#4caf50', ml: 1, fontSize: 'small' }} />
                      ) : (
                        <TrendingDown sx={{ color: '#ef5350', ml: 1, fontSize: 'small' }} />
                      )}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card
                  sx={{
                    borderRadius: 2,
                    bgcolor: light ? '#fff' : '#1e1e2f',
                    boxShadow: '0 6px 16px rgba(0,0,0,0.12)',
                    transition: 'transform 0.3s ease',
                    '&:hover': { transform: 'translateY(-4px)' },
                  }}
                >
                  <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <ShowChart sx={{ color: '#4caf50', fontSize: '1.5rem' }} />
                      <Typography variant="body2" sx={{ color: light ? 'grey.600' : 'grey.400' }}>
                        Win Rate
                      </Typography>
                    </Box>
                    <Typography
                      variant="h6"
                      sx={{
                        color: '#4caf50',
                        fontWeight: 600,
                        fontSize: { xs: '1.25rem', sm: '1.5rem' },
                      }}
                    >
                      {performanceMetrics.winRate}%
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card
                  sx={{
                    borderRadius: 2,
                    bgcolor: light ? '#fff' : '#1e1e2f',
                    boxShadow: '0 6px 16px rgba(0,0,0,0.12)',
                    transition: 'transform 0.3s ease',
                    '&:hover': { transform: 'translateY(-4px)' },
                  }}
                >
                  <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <TrendingUp sx={{ color: performanceMetrics.roi >= 0 ? '#4caf50' : '#ef5350', fontSize: '1.5rem' }} />
                      <Typography variant="body2" sx={{ color: light ? 'grey.600' : 'grey.400' }}>
                        ROI
                      </Typography>
                    </Box>
                    <Typography
                      variant="h6"
                      sx={{
                        color: performanceMetrics.roi >= 0 ? '#4caf50' : '#ef5350',
                        fontWeight: 600,
                        fontSize: { xs: '1.25rem', sm: '1.5rem' },
                      }}
                    >
                      {performanceMetrics.roi}%
                      {performanceMetrics.roi >= 0 ? (
                        <TrendingUp sx={{ color: '#4caf50', ml: 1, fontSize: 'small' }} />
                      ) : (
                        <TrendingDown sx={{ color: '#ef5350', ml: 1, fontSize: 'small' }} />
                      )}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card
                  sx={{
                    borderRadius: 2,
                    bgcolor: light ? '#fff' : '#1e1e2f',
                    boxShadow: '0 6px 16px rgba(0,0,0,0.12)',
                    transition: 'transform 0.3s ease',
                    '&:hover': { transform: 'translateY(-4px)' },
                  }}
                >
                  <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <ShowChart sx={{ color: performanceMetrics.averageProfitPerTrade >= 0 ? '#4caf50' : '#ef5350', fontSize: '1.5rem' }} />
                      <Typography variant="body2" sx={{ color: light ? 'grey.600' : 'grey.400' }}>
                        Avg. Profit/Trade
                      </Typography>
                    </Box>
                    <Typography
                      variant="h6"
                      sx={{
                        color: performanceMetrics.averageProfitPerTrade >= 0 ? '#4caf50' : '#ef5350',
                        fontWeight: 600,
                        fontSize: { xs: '1.25rem', sm: '1.5rem' },
                      }}
                    >
                      ${performanceMetrics.averageProfitPerTrade}
                      {performanceMetrics.averageProfitPerTrade >= 0 ? (
                        <TrendingUp sx={{ color: '#4caf50', ml: 1, fontSize: 'small' }} />
                      ) : (
                        <TrendingDown sx={{ color: '#ef5350', ml: 1, fontSize: 'small' }} />
                      )}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card
                  sx={{
                    borderRadius: 2,
                    bgcolor: light ? '#fff' : '#1e1e2f',
                    boxShadow: '0 6px 16px rgba(0,0,0,0.12)',
                    transition: 'transform 0.3s ease',
                    '&:hover': { transform: 'translateY(-4px)' },
                  }}
                >
                  <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <ShowChart sx={{ color: '#2196f3', fontSize: '1.5rem' }} />
                      <Typography variant="body2" sx={{ color: light ? 'grey.600' : 'grey.400' }}>
                        Total Trades
                      </Typography>
                    </Box>
                    <Typography
                      variant="h6"
                      sx={{
                        color: '#2196f3',
                        fontWeight: 600,
                        fontSize: { xs: '1.25rem', sm: '1.5rem' },
                      }}
                    >
                      {performanceMetrics.totalTrades}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </>
          )}
        </Grid>

        {/* Charts */}
        <Grid container spacing={2}>
          <Grid item xs={12} md={8}>
            <Card
              sx={{
                p: { xs: 2, sm: 3 },
                bgcolor: light ? '#fff' : '#1e1e2f',
                borderRadius: 3,
                boxShadow: '0 6px 12px rgba(0,0,0,0.1)',
              }}
            >
              <CardContent>
                <Typography
                  variant="h6"
                  sx={{ color: light ? 'grey.700' : 'grey.300', mb: 2, fontWeight: 500 }}
                >
                  Cumulative Profit/Loss Over Time
                </Typography>
                {loading ? (
                  <Box sx={{ bgcolor: light ? 'grey.200' : 'grey.700', height: 300, borderRadius: 2 }} />
                ) : performanceMetrics.cumulativeData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={performanceMetrics.cumulativeData}>
                      <XAxis
                        dataKey="date"
                        stroke={light ? 'grey.600' : 'grey.500'}
                        tick={{ fontSize: { xs: 10, sm: 12 } }}
                      />
                      <YAxis stroke={light ? 'grey.600' : 'grey.500'} tick={{ fontSize: { xs: 10, sm: 12 } }} />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: light ? '#fff' : '#1e1e2f',
                          border: `1px solid ${light ? 'grey.200' : 'grey.700'}`,
                          borderRadius: '8px',
                          fontSize: '0.875rem',
                        }}
                      />
                      <Legend wrapperStyle={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }} />
                      <Line
                        type="monotone"
                        dataKey="profit"
                        stroke={lineChartStroke}
                        name="Cumulative Profit"
                        strokeWidth={2}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <Typography
                    sx={{ color: light ? 'grey.600' : 'grey.500', textAlign: 'center', py: 8 }}
                  >
                    No data available for the selected period
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card
              sx={{
                p: { xs: 2, sm: 3 },
                bgcolor: light ? '#fff' : '#1e1e2f',
                borderRadius: 3,
                boxShadow: '0 6px 12px rgba(0,0,0,0.1)',
              }}
            >
              <CardContent>
                <Typography
                  variant="h6"
                  sx={{ color: light ? 'grey.700' : 'grey.300', mb: 2, fontWeight: 500 }}
                >
                  Trade Status Distribution
                </Typography>
                {loading ? (
                  <Box
                    sx={{
                      bgcolor: light ? 'grey.200' : 'grey.700',
                      width: 200,
                      height: 200,
                      borderRadius: '50%',
                      mx: 'auto',
                    }}
                  />
                ) : performanceMetrics.pieData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={performanceMetrics.pieData}
                        cx="50%"
                        cy="50%"
                        outerRadius={{ xs: 80, sm: 100 }}
                        dataKey="value"
                        label={{ fontSize: { xs: 10, sm: 12 } }}
                      >
                        {performanceMetrics.pieData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip
                        contentStyle={{
                          backgroundColor: light ? '#fff' : '#1e1e2f',
                          border: `1px solid ${light ? 'grey.200' : 'grey.700'}`,
                          borderRadius: '8px',
                          fontSize: '0.875rem',
                        }}
                      />
                      <Legend wrapperStyle={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <Typography
                    sx={{ color: light ? 'grey.600' : 'grey.500', textAlign: 'center', py: 8 }}
                  >
                    No data available
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </Fade>
  );
};

export default React.memo(AISection);